import React, { FC, MouseEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Chip from '../Chip/Chip';
import { Download, OpenInNew, QuestionMarkOutlined, Star, Language } from '@mui/icons-material';
import ArchiveIcon from '@mui/icons-material/Archive';
import Accordion from '../Accordion/Accordion';
import Helper from '../../shared/helpers/Helper';
import styled from 'styled-components';
import ReplyIcon from '@mui/icons-material/Reply';
import NotesIcon from '@mui/icons-material/Notes';
import DialogContentText from '@mui/material/DialogContentText';
import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import ChartComponentSurvey from '../ChartComponent/ChartComponentSurvey';
import SurveyService from '../../services/SurveyService';
import surveyService from '../../services/SurveyService';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { ISurveyStatistics } from '../../shared/interfaces/ISurveyStatistics';
import PieChartSurvey from '../../components/ChartComponent/PieChartSurvey';
import { ISurveysEntry } from '../../shared/interfaces/ISurveysEntry';
import useExportToExcel from '../../hooks/useExportToExcel';
import { AuthContext } from '../../providers/AuthProvider';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import SurveyToolGuide from '../../views/Research/SurveyTool/SurveyToolGuide/SurveyToolGuide';
import NetPromoterScoreGuide from '../../views/Research/SurveyTool/NetPromoterScoreGuide/NetPromoterScoreGuide';
import MoodBarometerGuide from '../../views/Research/SurveyTool/MoodBarometerGuide/MoodBarometerGuide';
import Guide from '../Guide/Guide';
import NoContent from '../../assets/images/monitoring_no_content.png';
import CloseIcon from '@mui/icons-material/Close';
import AppLogoImage from '../../assets/images/repraise-logo.png';
import EditIcon from '@mui/icons-material/Edit';
import ChartYearComponent from '../ChartComponent/ChartYearComponent';
import SurveyNpsGaugeChart from './SurveyNpsGaugeChart';
import SurveyUtils from '../../shared/SurveyUtils';
import SurveyNpsBreakdown from './SurveyNpsBreakdown';
import CustomSurveyGuide from '../../views/Research/SurveyTool/CustomSurveyGuide/CustomSurveyGuide';
import Confirm from '../Confirm/Confirm';
import SurveyLinkPopover from './SurveyLinkPopover';

export const AccordionSummaryContentBox = styled(Box)`
  display: flex;
  align-items: center;
`;

interface Props {
  survey: ISurveysEntry;
  activeSurveyCount: number;
  totalSurveyCount: number;
  toggleArchive: (id: string) => void;
  reload: () => Promise<void>;
}

const fetchImageAsBase64 = async (imagePath: string): Promise<string> => {
  const response = await fetch(imagePath);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result) {
        const base64String = (reader.result as string).split(';base64,').pop();
        resolve(base64String ?? '');
      } else {
        reject('Failed to convert image to Base64.');
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });
};

const Survey: FC<Props> = ({
  toggleArchive,
  survey,
  reload,
  activeSurveyCount,
  totalSurveyCount,
}) => {
  const {
    id,
    label,
    type,
    platform,
    companyName,
    slug,
    archived,
    createdAt,
    note,
    customAnswers,
    archivedAt,
    feedbackActive,
    updatedAt,
  } = survey;
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const { hasRole, exceedsLimit } = useContext(AuthContext);
  const exportToExcel = useExportToExcel();
  const theme = useTheme();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [noticeIsOpen, setNoticeIsOpen] = useState<boolean>(false);
  const [newNote, setNewNote] = useState<string>(note);
  const [loading, setLoading] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<ISurveyStatistics | null>(null);
  const [mode, setMode] = useState<FilterModeSurvey>(t('total') as FilterModeSurvey);
  const [kindOfSurvey, setKindOfSurvey] = useState<string>('');
  const [addCopySurvey, SetAddCopySurvey] = useState<boolean>(false);
  const [exitGuide, setExitGuide] = useState<boolean>(false);
  const [base64Image, setBase64Image] = useState<string>('');
  const [companyLogo, setCompanyLogo] = useState<string>('');
  const [editSurvey, setEditSurvey] = useState<boolean>(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState<boolean>(false);
  const [resetIsOpen, setResetIsOpen] = useState<boolean>(false);
  const [archiveIsOpen, setArchiveIsOpen] = useState<boolean>(false);

  const publicUrl = SurveyUtils.getPublicUrl(type, slug);

  useEffect(() => {
    fetchImageAsBase64(AppLogoImage).then((base64) => setBase64Image(base64 as string));
  }, []);
  // handlers start

  const getTypeName = (type: string) => {
    return SurveyUtils.getTypeName(type, t);
  };

  const handleExport = async () => {
    if (!statistics) return;
    setLoading(true);
    try {
      await exportToExcel(
        label,
        platform,
        companyName,
        slug,
        type,
        {
          answerOne: customAnswers?.answerOne,
          answerTwo: customAnswers?.answerTwo,
          answerThree: customAnswers?.answerThree,
          answerFour: customAnswers?.answerFour,
          answerFive: customAnswers?.answerFive,
        },
        base64Image,
        archived,
        archivedAt,
        survey.id,
        statistics
      );
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (event: MouseEvent<HTMLElement>, newMode: FilterModeSurvey | null) => {
    if (newMode) {
      setMode(newMode);
    }
  };

  const handleSurveyAction = async (action: Function, id: string) => {
    await action(id);
    await reload();
    const controller = new AbortController();
    await fetchData(controller);
    setResetIsOpen(false);
    setDeleteIsOpen(false);
  };

  const handleNoticeToggle = (shouldOpen: boolean) => {
    if (!shouldOpen && newNote === '') setNewNote('');
    setNoticeIsOpen(shouldOpen);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setNewNote(event.target.value);

  const handleSaveNote = async (id: string, note: string) => {
    await SurveyService.saveSurveyNote(id, note);
    handleNoticeToggle(false);
  };

  const handleEditQuestions = (id: string) => {
    navigate('/research/surveytool/questions/' + id);
  };

  const handleEditSurvey = async (type: string) => {
    const translatedKind = getTypeName(type);
    setKindOfSurvey(translatedKind);
    setEditSurvey(true);
    setExitGuide(true);
  };
  // handlers end

  const fetchData = async (controller: AbortController) => {
    try {
      const response = await SurveyService.getSurveyStatistics(id, controller.signal);
      setStatistics(response || null);
    } catch (error) {
      console.error('Error fetching survey responses:', error);
      setStatistics(null);
    }
  };

  //useEffect start
  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller);

    return () => controller.abort();
  }, [id, type]);

  useEffect(() => {
    setCurrentView(t('surveys'));
    document.title = `${t('app_branding')} ${t('surveys')}`;
  }, [setCurrentView, t]);
  //useEffect end

  // General Functions start
  const reloadAndClose = async () => {
    setExitGuide(false);
    setEditSurvey(false);
    await reload();
  };
  // General Functions end

  return (
    <>
      {(addCopySurvey && exitGuide) || editSurvey ? (
        <Box sx={{ border: '#357E8B 1px solid', borderRadius: '4px' }}>
          <Guide
            guideSelect={
              <>
                <Button
                  color={'error'}
                  sx={{ mb: theme.spacing(4) }}
                  onClick={() => {
                    setExitGuide(false);
                    setEditSurvey(false);
                  }}
                >
                  <CloseIcon /> {'Schließen'}
                </Button>
                <img
                  src={NoContent}
                  alt="empty-state"
                />
                <FormControl
                  size="small"
                  sx={{ mb: theme.spacing(4), minWidth: '17.75rem' }}
                >
                  <InputLabel id="select-label">{t('select_survey_type')}</InputLabel>
                  <Select
                    labelId="select-label"
                    label={t('select_survey_type')}
                    value={kindOfSurvey}
                    onChange={(e: SelectChangeEvent) => {
                      setKindOfSurvey(e.target.value);
                    }}
                  >
                    <MenuItem value="">
                      <em>---</em>
                    </MenuItem>
                    {hasRole('can-use-standard') && (
                      <MenuItem value={t('survey')}>{t('survey')}</MenuItem>
                    )}
                    <MenuItem value={t('nps_survey')}>{t('nps_survey')}</MenuItem>
                    {hasRole('can-use-mood') && (
                      <MenuItem value={t('mood_barometer')}>{t('mood_barometer')}</MenuItem>
                    )}
                    {hasRole('can-use-customsurvey') && (
                      <MenuItem value={t('custom_survey')}>{t('custom_survey')}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </>
            }
          >
            {kindOfSurvey === t('survey') && (
              <SurveyToolGuide
                id={id}
                isEdit={editSurvey}
                loadSurveyType={type}
                reload={reloadAndClose}
                hasAnswers={survey.totalCount > 0}
              />
            )}
            {kindOfSurvey === t('nps_survey') && (
              <NetPromoterScoreGuide
                id={id}
                isEdit={editSurvey}
                loadSurveyType={type}
                reload={reloadAndClose}
                hasAnswers={survey.totalCount > 0}
              />
            )}
            {kindOfSurvey === t('mood_barometer') && (
              <MoodBarometerGuide
                id={id}
                isEdit={editSurvey}
                loadSurveyType={type}
                hasAnswers={survey.totalCount > 0}
                reload={reloadAndClose}
              />
            )}
            {kindOfSurvey === t('custom_survey') && (
              <CustomSurveyGuide
                id={id}
                isEdit={editSurvey}
                hasAnswers={survey.totalCount > 0}
                loadSurveyType={type}
                reload={reloadAndClose}
              />
            )}
          </Guide>
        </Box>
      ) : (
        <Accordion
          expanded={isExpanded}
          onExpandRequest={() => setIsExpanded((prevState) => !prevState)}
          summary={
            <>
              <AccordionSummaryContentBox>
                <Checkbox
                  disabled
                  sx={{ display: 'none' }}
                />
                <Checkbox
                  disabled
                  checkedIcon={<Star sx={{ color: theme.palette.secondary.main }} />}
                  icon={<Star sx={{ color: theme.palette.text.disabled }} />}
                  sx={{ display: 'none', ml: theme.spacing(1) }}
                />
                {label}
                <Chip
                  size="small"
                  label={getTypeName(type)}
                  sx={{ ml: theme.spacing(2), my: theme.spacing(0.75) }}
                />
              </AccordionSummaryContentBox>
              <AccordionSummaryContentBox>
                <Tooltip
                  title={Helper.getFormattedDate(createdAt.toString(), false)}
                  arrow
                >
                  <Typography
                    variant="body2"
                    sx={{ mr: theme.spacing(1), cursor: 'pointer' }}
                  >
                    {createdAt === '01.01.0001'
                      ? Helper.getFormattedDate(createdAt.toString(), true)
                      : Helper.getFormattedDate(updatedAt.toString(), true)}
                  </Typography>
                </Tooltip>
                <SurveyLinkPopover
                  url={publicUrl}
                  slug={slug}
                />
                <Tooltip
                  title={publicUrl}
                  arrow
                >
                  <Link
                    href={publicUrl}
                    underline="none"
                    target="_blank"
                    rel="noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}
                  >
                    <OpenInNew fontSize={'small'} />
                  </Link>
                </Tooltip>
                {note === '' && newNote === '' ? (
                  <Tooltip
                    title={t('note')}
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleNoticeToggle(true)}
                      sx={{ ml: 'auto' }}
                    >
                      <NotesIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={t('edit_note')}
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleNoticeToggle(true)}
                    >
                      <DownloadDoneIcon
                        fontSize="small"
                        color={'success'}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Dialog
                  open={noticeIsOpen}
                  onClose={() => handleNoticeToggle(false)}
                  sx={{
                    '& .MuiDialog-paper': {
                      width: '100%',
                      maxWidth: 500,
                      maxHeight: '60%',
                      height: '45%',
                      margin: 0,
                      borderRadius: 0,
                    },
                    '& .MuiDialog-paperScrollPaper': {
                      maxHeight: '100%',
                    },
                  }}
                >
                  <DialogTitle>{t('note')}</DialogTitle>
                  <DialogContent>
                    <DialogContentText>{t('can_write')}</DialogContentText>
                    <TextField
                      id="outlined-multiline-static"
                      multiline
                      rows={10}
                      fullWidth
                      placeholder={t('note')}
                      margin={'normal'}
                      defaultValue={newNote}
                      onChange={handleTextChange}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => handleNoticeToggle(false)}>{t('cancel')}</Button>
                    <Button onClick={() => handleSaveNote(id, newNote)}>{t('save')}</Button>
                  </DialogActions>
                </Dialog>
                {!archived ? (
                  <Tooltip
                    title={t('archive_survey')}
                    arrow
                  >
                    <IconButton
                      color="success"
                      size="small"
                      onClick={() => setArchiveIsOpen(true)}
                      sx={{ ml: 'auto' }}
                    >
                      <ArchiveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={t('restore_survey')}
                    arrow
                  >
                    <IconButton
                      color="success"
                      size="small"
                      onClick={() => toggleArchive(id)}
                      sx={{ ml: 'auto' }}
                      disabled={exceedsLimit('limitsurveys', activeSurveyCount + 1)}
                    >
                      <ReplyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Confirm
                  text={t('archive_survey_confirm')}
                  open={archiveIsOpen}
                  onCancel={() => setArchiveIsOpen(false)}
                  onOk={() => {
                    setArchiveIsOpen(false);
                    toggleArchive(id);
                  }}
                />
                <Tooltip
                  title={t('survey_clear')}
                  arrow
                >
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => setResetIsOpen(true)}
                    sx={{ ml: 'auto' }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {type == 'CUSTOMSURVEY' && hasRole('can-use-surveylng') && (
                  <Tooltip
                    title={t('survey_localize')}
                    arrow
                  >
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => navigate('/research/surveytool/localize/' + id)}
                      sx={{ ml: 'auto' }}
                    >
                      <Language fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Confirm
                  text={t('reset_survey_confirm')}
                  open={resetIsOpen}
                  onCancel={() => setResetIsOpen(false)}
                  onOk={() => handleSurveyAction(SurveyService.resetSurvey, id)}
                />

                {survey.totalCount > 0 && (
                  <Tooltip
                    title={t('survey_download')}
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={handleExport}
                      sx={{ ml: 'auto' }}
                    >
                      {loading ? (
                        <LinearProgress sx={{ fontSize: theme.spacing(1) }} />
                      ) : (
                        <Download
                          fontSize="small"
                          color={'primary'}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                {!exceedsLimit('limitsurveys', totalSurveyCount + 1) && (
                  <Tooltip
                    title={t('survey_copy')}
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        setExitGuide(true);
                        if (type === 'NPS') {
                          SetAddCopySurvey(true);
                          setKindOfSurvey(t('nps_survey'));
                        }
                        if (type === 'STANDARD') {
                          SetAddCopySurvey(true);
                          setKindOfSurvey(t('survey'));
                        }
                        if (type === 'BAROMETER') {
                          SetAddCopySurvey(true);
                          setKindOfSurvey(t('mood_barometer'));
                        }
                        if (type === 'CUSTOMSURVEY') {
                          SetAddCopySurvey(true);
                          setKindOfSurvey(t('custom_survey'));
                        }
                      }}
                      sx={{ ml: 'auto' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={t('survey_edit')}
                  arrow
                >
                  <IconButton
                    size="small"
                    sx={{ ml: 'auto' }}
                    onClick={() => handleEditSurvey(type)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {survey.totalCount === 0 && type == 'CUSTOMSURVEY' && (
                  <Tooltip
                    title={t('survey_questions_edit')}
                    arrow
                  >
                    <IconButton
                      size="small"
                      sx={{ ml: 'auto' }}
                      onClick={() => handleEditQuestions(id)}
                    >
                      <QuestionMarkOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={t('survey_delete')}
                  arrow
                >
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => setDeleteIsOpen(true)}
                    sx={{ ml: 'auto' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Confirm
                  text={t('delete_confirm')}
                  open={deleteIsOpen}
                  onCancel={() => setDeleteIsOpen(false)}
                  onOk={() => handleSurveyAction(SurveyService.deleteSurvey, id)}
                />
              </AccordionSummaryContentBox>
            </>
          }
          details={
            <>
              {isExpanded && (
                <>
                  <Divider />

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        justifyContent: 'center',
                        mt: theme.spacing(2),
                      }}
                    >
                      <ToggleButtonGroup
                        value={mode}
                        color="primary"
                        size="small"
                        exclusive
                        onChange={handleModeChange}
                        aria-label="text alignment"
                        sx={{
                          my: theme.spacing(2),
                          ml: 'auto',
                          '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                            border: `1px solid ${theme.palette.primary.main}`,
                          },
                        }}
                      >
                        <ToggleButton
                          value={t('total') as FilterModeSurvey}
                          aria-label={t('total')}
                        >
                          {t('total')}
                        </ToggleButton>
                        {type === 'STANDARD' && (
                          <ToggleButton
                            value="details"
                            aria-label="details"
                          >
                            DETAILS
                          </ToggleButton>
                        )}
                        <Tooltip
                          title={
                            <>
                              <Typography variant="body2">
                                {'Auswertung der letzten 12 Monate'}
                              </Typography>
                            </>
                          }
                        >
                          <ToggleButton
                            value="month"
                            aria-label="month"
                          >
                            {t('month')}
                          </ToggleButton>
                        </Tooltip>
                      </ToggleButtonGroup>

                      {mode === 'details' && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          {statistics?.totalAnswers && mode === 'details' ? (
                            <PieChartSurvey
                              statistics={statistics}
                              type={type}
                              mode={mode}
                              customAnswers={customAnswers}
                            />
                          ) : (
                            <Alert
                              severity="warning"
                              sx={{
                                minWidth: '450px',
                                maxWidth: '650px',
                                alignSelf: 'center',
                                my: theme.spacing(1),
                              }}
                            >
                              <AlertTitle sx={{ fontWeight: '700' }}>{t('no_records')}</AlertTitle>
                              {t('no_records_found')}
                            </Alert>
                          )}
                        </Box>
                      )}

                      {mode === 'month' && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            height: '20vh',
                            flexGrow: 1,
                          }}
                        >
                          {statistics?.totalAnswers && mode === 'month' ? (
                            <ChartYearComponent
                              statistics={statistics}
                              type={type as string}
                              mode={mode as string}
                            />
                          ) : (
                            <Alert
                              severity="warning"
                              sx={{
                                minWidth: '450px',
                                maxWidth: '650px',
                                alignSelf: 'center',
                                my: theme.spacing(1),
                              }}
                            >
                              <AlertTitle sx={{ fontWeight: '700' }}>{t('no_records')}</AlertTitle>
                              {t('no_records_found')}
                            </Alert>
                          )}
                        </Box>
                      )}

                      {mode === t('total') && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            height: '20vh',
                            flexGrow: 1,
                          }}
                        >
                          {statistics?.totalAnswers && mode === t('total') ? (
                            <ChartComponentSurvey
                              statistics={statistics}
                              type={type}
                              mode={mode}
                            />
                          ) : (
                            <Alert
                              severity="warning"
                              sx={{
                                minWidth: '450px',
                                maxWidth: '650px',
                                alignSelf: 'center',
                                my: theme.spacing(1),
                              }}
                            >
                              <AlertTitle sx={{ fontWeight: '700' }}>{t('no_records')}</AlertTitle>
                              {t('no_records_found')}
                            </Alert>
                          )}
                        </Box>
                      )}
                    </Box>

                    {type === 'NPS' && <SurveyNpsGaugeChart survey={survey}></SurveyNpsGaugeChart>}
                    {type === 'NPS' && <SurveyNpsBreakdown survey={survey}></SurveyNpsBreakdown>}
                  </Box>
                  <Divider />
                </>
              )}
            </>
          }
        />
      )}
    </>
  );
};

export default Survey;
